import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Icon } from '@mui/material';
import { actionItemIcons, customColors, vehicleString } from '../../utilities';
import {
  getTranslationKey,
  humanizeEnumBasic,
  keys,
} from '../../utilities/translator/translation_keys';
import { Text } from '../typography';
import {
  priorityStringLookup,
  statusStringLookup,
} from '../action_items/action_item_utilities';
import MultiAvatar from '../MultiAvatar';
import SimpleWorkspaceCard from '../SimpleWorkspaceCard';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { useCurrentUser } from '../../providers/UserProvider';
import { useTableComponents } from './MTableComponents';
import styles from './MTableComponents.module.scss';
import {
  defaultAssessmentTypes,
  getColor,
  incidentStatus,
} from '../../constants/strings';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  cpaKeys,
  cpaStatusTypes,
  extensionStatusTypes,
  getAuditStatus,
  riskColors,
  riskLevels,
} from '../../utilities/audits';
import Label from '../action_items/Label';
import {
  observationStatus,
  observationTypes,
} from '../../utilities/observations';
import { getLabel, incidentStatusTypes } from '../../utilities/incidents';
import { useWorkspacePermissions } from '../../providers/WorkspacePermissionsProvider';
import SimpleUserCard from '../SimpleUserCard';
import noImage from '../../assets/icons/image_blank.png';
import TextWithIcon from '../typography/TextWithIcon';
import DropdownCard from '../dropdownV2/DropdownCard';
import { useTemplates } from '../../providers/TemplateProvider';
import { calculateRisk } from '../../utilities/riskMatrix';
import { timecardStatusTypes } from '../../pages/hr/Timecards';
import { expenseStatusTypes, expenseTopInputs } from '../../pages/hr/Expenses';
import { daysSince } from '../../utilities/time';

const expenseTypes = expenseTopInputs[1]?.options;
const cellProps = { weight: 'regular', size: 'xs', noMargin: true };
const groupedProps = { weight: 'semiBold', size: 'sm', noMargin: true };

function resolveTemplateFamilyTitle(templateId, allTemplates) {
  const own = allTemplates.find((t) => t.id === templateId);
  if (!own) return null;
  const rootId = own.parentId ?? own.id;
  const family = allTemplates.filter(
    (t) => t.id === rootId || t.parentId === rootId,
  );
  const latest = family.length
    ? family.reduce((a, b) =>
        (b.revisionNumber ?? -1) > (a.revisionNumber ?? -1) ? b : a,
      )
    : own;
  return latest.title;
}

export function useMTableColumns({ dateFormat = 'MMM Do, YYYY' } = {}) {
  const { t } = useTranslation();
  const { availableWorkspaces, allVehicles } = useWorkspace();
  const { allTemplateTypes, user } = useCurrentUser();
  const { allUsers } = useWorkspacePermissions();
  const { allTemplates } = useTemplates();

  const {
    dateCell,
    labelCell,
    groupedCell,
    textCell,
    workspaceCell,
    multiLabelCell,
    externalIdCell,
    labelCellWithIcon,
    dateCellFromNow,
  } = useTableComponents();

  const { disableAssessmentSubject } = useFlags();

  return useMemo(() => {
    const registry = {
      private: {
        accessorKey: 'private',
        header: undefined,
        size: 50,
        filterVariant: 'select',
        filterSelectOptions: [
          { label: 'Private', value: `${true}` },
          { label: 'Public', value: `${false}` },
        ],
        Cell: ({ cell }) => {
          return (
            <Icon
              baseClassName="material-icons-outlined"
              sx={{
                opacity: 0.5,
                fontSize: '1rem',
              }}
            >
              {!cell.getValue() ? 'visibility' : 'visibility_off'}
            </Icon>
          );
        },
        GroupedCell: ({ cell, row }) => {
          const component = (
            <div className={styles.paddingSm}>
              <TextWithIcon
                weight="semiBold"
                leftIcon={!cell.getValue() ? 'visibility' : 'visibility_off'}
                iconProps={{
                  opacity: 0.5,
                  fontSize: '1rem',
                }}
                size="xs"
              >
                {!cell.getValue() ? 'Public' : 'Private'}
              </TextWithIcon>
            </div>
          );
          return groupedCell({ component, row });
        },
      },
      actionItemType: {
        accessorKey: 'type',
        header: 'Type',
        size: 80,
        enableExpanding: false,
        Cell: ({ cell, row }) => {
          const isObservation =
            row.original.correctiveAction?.isObservation || false;
          const value = String(cell.getValue() ?? '').toLowerCase();
          const isRepeat = !!row.original.correctiveAction?.parentId;
          const meta = actionItemIcons(value, allTemplateTypes);
          const title =
            t(keys.common[cell.getValue()]) ||
            t(getTranslationKey(cell.getValue(), 'common')) ||
            humanizeEnumBasic(cell.getValue()) ||
            cell.getValue();

          return (
            <div className={styles.groups}>
              <Icon
                baseClassName="material-icons-outlined"
                sx={{ fontSize: '1rem', color: meta?.color }}
              >
                {isRepeat
                  ? 'repeat'
                  : isObservation
                  ? 'person_search'
                  : meta?.icon || 'cancel'}
              </Icon>
              <Text {...cellProps}>
                {title === 'Vehicle' ? 'Fleet' : title}
              </Text>
            </div>
          );
        },
        GroupedCell: ({ cell, row }) => {
          const isObservation =
            row.original.correctiveAction?.isObservation || false;
          const value = String(cell.getValue() ?? '').toLowerCase();
          const isRepeat = !!row.original.correctiveAction?.parentId;
          const meta = actionItemIcons(value, allTemplateTypes);
          const title =
            t(keys.common[cell.getValue()]) ||
            t(getTranslationKey(cell.getValue(), 'common')) ||
            humanizeEnumBasic(cell.getValue()) ||
            cell.getValue();
          const component = (
            <div className={styles.groups}>
              <Icon
                baseClassName="material-icons-outlined"
                sx={{ fontSize: '1rem', color: meta?.color }}
              >
                {isRepeat
                  ? 'repeat'
                  : isObservation
                  ? 'person_search'
                  : meta?.icon || 'cancel'}
              </Icon>
              <Text {...groupedProps}>
                {title === 'Vehicle' ? 'Fleet' : title}
              </Text>
            </div>
          );
          return groupedCell({ component, row });
        },
      },
      dateCreatedFromNow: {
        accessorKey: 'dateCreated',
        header: 'Created',
        size: 90,
        Cell: ({ cell }) =>
          dateCellFromNow({ cell, weight: 'regular', size: 'xs' }),
        GroupedCell: ({ cell }) =>
          dateCellFromNow({ cell, weight: 'regular', size: 'sm' }),
      },
      dateCreated: {
        accessorKey: 'dateCreated',
        size: 90,
        header: 'Created',
        Cell: (props) =>
          dateCell(props, {
            format: dateFormat,
            weight: 'regular',
            size: 'xs',
          }),
      },
      dateCompleted: {
        accessorKey: 'dateCompleted',
        size: 90,
        header: 'Completed',
        Cell: (props) =>
          dateCell(props, {
            format: dateFormat,
            weight: 'regular',
            size: 'xs',
          }),
      },
      dateModified: {
        accessorKey: 'dateModified',
        size: 90,
        header: 'Updated',
        Cell: (props) =>
          dateCell(props, {
            format: dateFormat,
            weight: 'regular',
            size: 'xs',
          }),
      },
      actionItemStatus: {
        accessorKey: 'status',
        header: 'Status',
        size: 75,
        Cell: ({ cell }) => {
          const s = statusStringLookup?.[cell.getValue()];
          if (!s) return null;
          return labelCell({ name: t(s.title), color: s.color });
        },
        GroupedCell: ({ cell, row }) => {
          const s = statusStringLookup?.[cell.getValue()];
          if (!s) return null;
          const component = labelCell({
            name: t(s.title),
            color: s.color,
          });
          return groupedCell({ component, row });
        },
      },
      priority: {
        accessorKey: 'priority',
        header: 'Priority',
        size: 75,
        Cell: ({ cell }) => {
          const p = priorityStringLookup?.[cell.getValue()];
          if (!p) return null;
          return labelCell({ name: t(p.name), color: p.color });
        },
        GroupedCell: ({ cell, row }) => {
          const p = priorityStringLookup?.[cell.getValue()];
          if (!p) return null;
          const component = labelCell({
            name: t(p.name),
            color: p.color,
          });
          return groupedCell({ component, row });
        },
      },
      creatorId: {
        accessorKey: 'creatorId',
        header: 'Creator',
        size: 100,
        Cell: ({ cell }) => {
          const userId = cell.getValue();
          const user = allUsers?.find((u) => u.id === userId);
          return !!user?.id ? (
            <SimpleUserCard user={user} weight="regular" size="xs" />
          ) : (
            <DropdownCard
              icon="person_off"
              title={t(keys.observations.ANONYMOUS)}
              size="xs"
              weight="regular"
              iconProps={{ fontSize: '1rem' }}
            />
          );
        },
        GroupedCell: ({ cell, row }) => {
          const userId = cell.getValue();
          const user = allUsers?.find((u) => u.id === userId);
          const component = !!user?.id ? (
            <SimpleUserCard user={user} weight="semiBold" size="sm" />
          ) : (
            <DropdownCard
              size="sm"
              icon="person_off"
              title={t(keys.observations.ANONYMOUS)}
              iconProps={{ fontSize: '1.25rem' }}
            />
          );
          return groupedCell({ component, row });
        },
      },
      assigneeId: {
        accessorKey: 'assigneeId',
        header: 'Assignee',
        size: 100,
        Cell: ({ cell }) => {
          const userId = cell.getValue();
          const user = allUsers?.find((u) => u.id === userId);
          return <SimpleUserCard user={user} weight="regular" size="xs" />;
        },
        GroupedCell: ({ cell, row }) => {
          const userId = cell.getValue();
          const user = allUsers?.find((u) => u.id === userId);
          const component = (
            <SimpleUserCard
              user={user}
              weight="semiBold"
              mediumAvatar
              size="sm"
            />
          );
          return groupedCell({ component, row });
        },
      },
      participants: {
        accessorKey: 'participants',
        header: 'Participants',
        size: 100,
        Cell: ({ cell }) => {
          const users = cell.getValue();
          return users?.length ? (
            <div className={styles.participants}>
              <MultiAvatar weight="regular" size="xs" users={users} />
            </div>
          ) : (
            <Text size="xs" color="secondary" noMargin>
              {t(keys.common.NONE)}
            </Text>
          );
        },
        GroupedCell: ({ cell, row }) => {
          const users = cell.getValue();
          const component = users?.length ? (
            <MultiAvatar weight="regular" size="xs" users={users} />
          ) : (
            <Text size="sm" color="secondary" noMargin>
              {t(keys.common.NONE)}
            </Text>
          );
          return groupedCell({ component, row });
        },
      },
      dueDate: {
        accessorKey: 'dueDate',
        header: 'Due Date',
        enableGrouping: true,
        size: 120,
        Cell: (props) =>
          dateCell(props, {
            format: dateFormat,
            weight: 'regular',
            size: 'xs',
          }),
        GroupedCell: ({ cell, row }) => {
          const component = dateCell(
            { cell },
            {
              format: dateFormat,
              weight: 'semiBold',
              size: 'sm',
            },
          );
          return groupedCell({ component, row });
        },
      },
      dateOfIncident: {
        accessorKey: 'dateOfIncident',
        header: t(keys.incidents.DATE_OF_INCIDENT),
        enableGrouping: true,
        size: 120,
        // Time of day matters for incidents specifically (a cluster at the
        // same time of day can be a signal) — unlike dueDate/most other date
        // columns here, this one always shows it, so it doesn't fall back to
        // the generic day-only dateFormat default. Matches the format used
        // for this same field in graph labels (reports.js's useReportLabel)
        // and the report builder's DATA-mode column (ReportBuildMenuData.jsx).
        Cell: (props) =>
          dateCell(props, {
            format: 'MMM D, YYYY h:mm A',
            weight: 'regular',
            size: 'xs',
          }),
        GroupedCell: ({ cell, row }) => {
          const component = dateCell(
            { cell },
            {
              format: 'MMM D, YYYY h:mm A',
              weight: 'semiBold',
              size: 'sm',
            },
          );
          return groupedCell({ component, row });
        },
      },
      workspaceId: {
        header: 'Workspace',
        accessorKey: 'workspaceId',
        enableExpanding: false,
        enableGrouping: true,
        size: 100,
        GroupedCell: ({ cell, row }) => {
          const workspace = availableWorkspaces.find(
            (w) => w.id === cell.getValue(),
          );
          const component = (
            <SimpleWorkspaceCard workspace={workspace} size="sm" mediumAvatar />
          );
          return groupedCell({ component, row });
        },
        Cell: ({ cell }) => {
          const workspace = availableWorkspaces.find(
            (w) => w.id === cell.getValue(),
          );
          return (
            <SimpleWorkspaceCard
              weight="regular"
              size="xs"
              workspace={workspace}
            />
          );
        },
      },
      inspectionType: {
        accessorKey: 'inspectionType',
        header: t(keys.common.TYPE),
        size: 90,
        filterVariant: 'select',
        Cell: ({ cell }) => {
          const type = cell.getValue()?.toLowerCase() || '';
          const color = getColor(type, allTemplateTypes);
          const props =
            type === 'vehicle'
              ? defaultAssessmentTypes.fleet
              : allTemplateTypes[type] || defaultAssessmentTypes[type];
          return (
            <DropdownCard
              size="xs"
              weight="regular"
              title={props?.title || props?.name || t(keys.common.INVALID)}
              icon={props?.icon || 'star_border'}
              iconProps={{ color, fontSize: '1rem' }}
            />
          );
        },
        GroupedCell: ({ cell, row }) => {
          const type = cell.getValue()?.toLowerCase() || '';
          const color = getColor(type, allTemplateTypes);
          const props =
            type === 'vehicle'
              ? defaultAssessmentTypes.fleet
              : allTemplateTypes[type] || defaultAssessmentTypes[type];
          const component = (
            <DropdownCard
              size="sm"
              weight="semiBold"
              title={props?.title || props?.name || t(keys.common.INVALID)}
              icon={props?.icon || 'star_border'}
              iconProps={{ color }}
            />
          );
          return groupedCell({ component, row });
        },
      },
      templateTitle: {
        accessorFn: (row) =>
          resolveTemplateFamilyTitle(row.templateId, allTemplates) ??
          'No Title',
        id: 'templateId',
        header: t(keys.common.TITLE),
        size: 120,
        Cell: ({ row }) => {
          const template = allTemplates.find(
            (t) => t.id === row.original.templateId,
          );
          return <Text {...cellProps}>{template?.title || 'No Title'}</Text>;
        },
        GroupedCell: ({ cell, row }) => {
          const component = (
            <Text {...groupedProps}>{cell.getValue() || 'No Title'}</Text>
          );
          return groupedCell({ component, row });
        },
      },
      subject: {
        accessorKey: 'subject',
        header: 'Subject',
        size: 100,
        show: !disableAssessmentSubject,
        Cell: ({ cell }) => {
          return <Text {...cellProps}>{cell.getValue()}</Text>;
        },
      },
      isDraft: {
        accessorKey: 'isDraft',
        header: t(keys.common.STATUS),
        size: 75,
        Header: ({ column }) => (
          <div style={{ textWrap: 'nowrap' }}>{column.columnDef.header}</div>
        ),
        Cell: ({ row }) => {
          const label = getAuditStatus({
            inspection: row.original,
            actionItems: row.original.actionItems,
          });
          return labelCell({
            name: t(label.title),
            color: label.color,
          });
        },
        GroupedCell: ({ row }) => {
          const label = getAuditStatus({
            inspection: row.original,
            actionItems: row.original.actionItems,
          });
          const component = labelCell({
            name: t(label.title),
            color: label.color,
            rowSized: false,
          });
          return groupedCell({ component, row });
        },
      },
      passed: {
        accessorKey: 'passed',
        header: t(keys.common.PASSED),
        filterVariant: 'select',
        size: 50,
        filterSelectOptions: [
          { value: 'true', label: t(keys.common.PASSED) },
          { value: 'false', label: t(keys.common.FAILED) },
        ],
        Cell: ({ cell }) => (
          <Icon
            baseClassName="material-icons-outlined"
            sx={{
              color: cell.getValue() ? '#28a745' : '#e9222c',
              fontSize: '1.25rem',
            }}
          >
            {cell.getValue() ? 'check' : 'close'}
          </Icon>
        ),
        GroupedCell: ({ cell, row }) => {
          const name = !!cell.getValue()
            ? t(keys.common.PASSED)
            : t(keys.common.FAILED);
          const color = !!cell.getValue() ? 'green' : 'red';
          const rowSized = false;

          const component = labelCell({
            name,
            color,
            rowSized,
          });
          return groupedCell({ component, row });
        },
      },
      vehicleId: {
        accessorKey: 'vehicleId',
        header: t(keys.common.ASSET),
        size: 120,
        enableSorting: false,
        Cell: ({ cell }) => {
          const vehicleId = cell.getValue();
          const vehicle = allVehicles.find((v) => v.id === vehicleId);
          return vehicle?.id ? (
            <div className={styles.workspace}>
              <img
                className={styles.workspaceLogo}
                src={cell.getValue()?.imageUrl || noImage}
                alt="vehicle"
              />
              <Text noMargin size="xs" truncate weight={'regular'}>
                {vehicleString(vehicle)}
              </Text>
            </div>
          ) : (
            <Text noMargin color="secondary" size="xs">
              {t(keys.common.NONE)}
            </Text>
          );
        },
        GroupedCell: ({ cell, row }) => {
          const vehicleId = cell.getValue();
          const vehicle = allVehicles.find((v) => v.id === vehicleId);
          const component = vehicle?.id ? (
            <div className={styles.workspace}>
              <img
                className={styles.workspaceLogo}
                src={cell.getValue()?.imageUrl || noImage}
                alt="vehicle"
              />
              <Text {...groupedProps}>{vehicleString(vehicle)}</Text>;
            </div>
          ) : (
            <Text noMargin color="secondary" size="sm">
              No Asset
            </Text>
          );
          return groupedCell({ component, row });
        },
      },
      workspace: {
        size: 120,
        accessorKey: 'workspace',
        header: t(keys.common.WORKSPACE),
        enableSorting: false,
        Cell: ({ cell }) =>
          workspaceCell({ cell, weight: 'regular', size: 'xs' }),
        GroupedCell: ({ cell, row }) => {
          const component = (
            <SimpleWorkspaceCard size="sm" workspace={cell.getValue()} />
          );
          return groupedCell({ component, row });
        },
      },
      labels: {
        accessorKey: 'labels',
        size: 100,
        header: t(keys.common.LABELS),
        enableSorting: false,
        Cell: ({ cell }) => {
          const labels = cell.getValue() || [];
          return multiLabelCell({ labels });
        },
        GroupedCell: ({ cell, row }) => {
          const values = cell.getValue();
          const labels = [];
          for (const v of values) {
            labels.push(
              <Label
                color={v?.color}
                name={v?.name}
                rowSized
                truncate={false}
              />,
            );
          }
          const component = (
            <div className={styles.labelGroupCell}>
              {labels?.length > 0 ? labels : t(keys.common.NONE)}
            </div>
          );
          return groupedCell({ component, row });
        },
      },
      inspectionSignatures: {
        accessorKey: 'attachments',
        header: `${t(keys.common.SIGNATURE)}s`,
        enableSorting: false,
        enableColumnFilter: false,
        size: 100,
        Cell: ({ cell }) => {
          const signatures = cell.getValue() || [];
          const users = signatures
            ?.filter((s) => s.extension === 'SIGNATURE')
            ?.map((u) => u.creator);
          return (
            <div style={{ marginLeft: '0.4rem' }}>
              <MultiAvatar users={users} maxLength={5} size="xs" />
            </div>
          );
        },
      },
      type: {
        accessorKey: 'type',
        header: t(keys.common.TYPE),
        size: 100,
        Cell: ({ cell }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            t(getTranslationKey(cell.getValue(), 'common')) ||
            cell.getValue();
          return <Text {...cellProps}>{string}</Text>;
        },
        GroupedCell: ({ cell, row }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            t(getTranslationKey(cell.getValue(), 'common')) ||
            cell.getValue();
          const component = <Text {...cellProps}>{string}</Text>;
          return groupedCell({ component, row });
        },
      },
      billableHours: {
        accessorKey: 'billableHours',
        header: t(keys.timecards.HOURS),
        size: 75,
        Cell: ({ cell }) => textCell({ cell, size: 'xs', weight: 'regular' }),
      },
      expenseType: {
        accessorKey: 'type',
        header: t(keys.common.TYPE),
        size: 75,
        Cell: ({ cell }) => {
          const string = t(
            expenseTypes?.find((t) => t.key === cell.getValue())?.text,
          );
          return <Text {...cellProps}>{string}</Text>;
        },

        GroupedCell: ({ cell, row }) => {
          const string = t(
            expenseTypes?.find((t) => t.key === cell.getValue())?.text,
          );
          const component = <Text {...groupedProps}>{string}</Text>;

          return groupedCell({ component, row });
        },
      },
      total: {
        accessorKey: 'total',
        header: t(keys.expenses.TOTAL),
        size: 75,
        Cell: ({ cell }) => textCell({ cell, size: 'xs' }),
        GroupedCell: ({ cell, row }) => {
          const string = t(getTranslationKey(cell.getValue(), 'expenses'));
          const component = <Text {...groupedCell}>{string}</Text>;
          return groupedCell({ component, row });
        },
      },
      subtype: {
        accessorKey: 'subtype',
        header: t(keys.common.SUBTYPE),
        size: 75,
        Cell: ({ cell }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            cell.getValue();
          return (
            <Text size="xs" noMargin>
              {string}
            </Text>
          );
        },
        GroupedCell: ({ cell, row }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            cell.getValue();

          const component = (
            <Text noMargin size="sm" weight="semiBold">
              {string}
            </Text>
          );
          return groupedCell({ component, row });
        },
      },
      subType: {
        accessorKey: 'subType',
        header: t(keys.common.SUBTYPE),
        size: 100,
        Cell: ({ cell }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            cell.getValue();
          return <Text {...cellProps}>{string}</Text>;
        },
        GroupedCell: ({ cell, row }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            cell.getValue();

          const component = <Text {...groupedCell}>{string}</Text>;
          return groupedCell({ component, row });
        },
      },
      incidentType: {
        accessorKey: 'type',
        header: t(keys.common.TYPE),
        size: 100,
        Cell: ({ cell }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            cell.getValue();
          return <Text {...cellProps}>{string}</Text>;
        },
        GroupedCell: ({ cell, row }) => {
          const string =
            t(getTranslationKey(cell.getValue(), 'incidents')) ||
            t(getTranslationKey(cell.getValue(), 'observations')) ||
            cell.getValue();
          const component = <Text {...groupedProps}>{string}</Text>;
          return groupedCell({ component, row });
        },
      },
      incidentStatus: {
        accessorKey: 'status',
        header: t(keys.common.STATUS),
        size: 95,
        filterVariant: 'select',
        filterSelectOptions: [
          ...Object.keys(incidentStatusTypes)
            .filter((s) => s !== 'INVALID')
            .map((s) => ({
              value: s,
              label: t(getTranslationKey(s, 'incidents')),
            })),
        ],
        Cell: ({ cell, row }) => {
          const status = row.original.status;
          const remainingComments = row.original.actionItems?.some(
            (a) =>
              a.type === 'INCIDENT_COMMENT' &&
              a.status !== 'DONE' &&
              !a.isArchived,
          );
          const openActionItems =
            row.original.actionItems?.filter(
              (a) =>
                a.status !== 'DONE' &&
                a.status !== 'DUPLICATE' &&
                a.type !== 'INCIDENT_COMMENT' &&
                !a.isArchived,
            ) || [];

          let name = getLabel(cell.getValue() || '');
          let color = incidentStatusTypes[cell.getValue()]?.color;
          let showIcon = false;
          if (!!remainingComments && status !== incidentStatus.complete) {
            name = 'Pending Comment';
            color = 'orange';
            const requiresComment = row.original.actionItems.find(
              (a) =>
                a.type === 'INCIDENT_COMMENT' &&
                a.status !== 'DONE' &&
                !a.isArchived &&
                a.assigneeId === user?.id,
            );

            showIcon = !!requiresComment;
          }
          if (
            openActionItems?.length > 0 &&
            status === incidentStatus.complete
          ) {
            name = 'Pending Action';
            color = 'orange';
          }
          return labelCellWithIcon({
            name,
            labelColor: color,
            show: showIcon,
            icon: 'error_outlined',
            iconColor: 'red',
          });
        },
        GroupedCell: ({ row, cell }) => {
          const status = row.original.status;
          const remainingComments = row.original.actionItems?.some(
            (a) =>
              a.type === 'INCIDENT_COMMENT' &&
              a.status !== 'DONE' &&
              !a.isArchived,
          );
          const openActionItems =
            row.original.actionItems?.filter(
              (a) =>
                a.status !== 'DONE' &&
                a.status !== 'DUPLICATE' &&
                a.type !== 'INCIDENT_COMMENT' &&
                !a.isArchived,
            ) || [];

          let name = getLabel(cell.getValue() || '');
          let color = incidentStatusTypes[cell.getValue()]?.color;
          let showIcon = false;
          if (!!remainingComments && status !== incidentStatus.complete) {
            name = 'Pending Comment';
            color = 'orange';
            const requiresComment = row.original.actionItems.find(
              (a) =>
                a.type === 'INCIDENT_COMMENT' &&
                a.status !== 'DONE' &&
                !a.isArchived &&
                a.assigneeId === user?.id,
            );

            showIcon = !!requiresComment;
          }
          if (
            openActionItems?.length > 0 &&
            status === incidentStatus.complete
          ) {
            name = 'Pending Action';
            color = 'orange';
          }
          const component = labelCellWithIcon({
            name,
            labelColor: color,
            show: showIcon,
            icon: 'error_outlined',
            iconColor: 'red',
          });
          return groupedCell({ component, row });
        },
      },
      negative: {
        accessorKey: 'negative',
        header: '',
        size: 50,
        Cell: ({ cell, row }) => {
          const key = cell.getValue() === true ? 'NEGATIVE' : 'POSITIVE';
          const value = {
            icon: observationTypes[key].icon,
            color: observationTypes[key].color,
          };
          return (
            <Icon opacity={0.5} color={value.color}>
              {value.icon}
            </Icon>
          );
        },
      },
      recognition: {
        accessorKey: 'recognition',
        header: '',
        size: 50,
        filterVariant: 'select',
        filterSelectOptions: [
          { value: 'TRUE', label: t(keys.observations.RECOGNITION) },
          { value: 'FALSE', label: t(keys.observations.NO_RECOGNITION) },
        ],
        Cell: ({ cell }) => {
          return !!cell.getValue() ? (
            <Icon style={{ fontSize: '1.2rem', color: customColors.YELLOW }}>
              hotel_class
            </Icon>
          ) : (
            ''
          );
        },
      },
      externalId: {
        accessorKey: 'externalId',
        size: 75,
        header: 'ID',
        Cell: ({ row }) =>
          externalIdCell({
            externalId: row.original?.externalId,
            id: row.original?.id,
            size: 'xs',
            weight: 'regular',
          }),
      },
      title: {
        accessorKey: 'title',
        size: 100,
        header: t(keys.common.TITLE),
        Cell: ({ cell }) => textCell({ cell, weight: 'regular', size: 'xs' }),
        GroupedCell: ({ cell }) =>
          textCell({ cell, weight: 'semiBold', size: 'sm' }),
      },
      description: {
        accessorKey: 'description',
        size: 100,
        header: t(keys.common.DESCRIPTION),
        Cell: ({ cell }) => textCell({ cell, weight: 'regular', size: 'xs' }),
        GroupedCell: ({ cell }) =>
          textCell({ cell, weight: 'semiBold', size: 'sm' }),
      },
      observationStatus: {
        accessorKey: 'status',
        header: t(keys.common.STATUS),
        size: 90,
        Cell: ({ cell }) => {
          return cell.getValue() ? (
            <Label
              noMargin
              name={
                t(
                  getTranslationKey(
                    observationStatus[cell.getValue()]?.title,
                    'common',
                  ),
                ) ||
                observationStatus[cell.getValue()]?.title ||
                cell.getValue()
              }
              weight="semiBold"
              color={
                !cell.getValue()
                  ? 'green'
                  : observationStatus[cell.getValue()]?.color || 'primary'
              }
            />
          ) : null;
        },
        GroupedCell: ({ cell, row }) => {
          const component = cell.getValue() ? (
            <Label
              noMargin
              rowSized={false}
              name={
                t(
                  getTranslationKey(
                    observationStatus[cell.getValue()]?.title,
                    'common',
                  ),
                ) ||
                observationStatus[cell.getValue()]?.title ||
                cell.getValue()
              }
              weight="semiBold"
              color={
                !cell.getValue()
                  ? 'green'
                  : observationStatus[cell.getValue()]?.color || 'primary'
              }
            />
          ) : null;
          return groupedCell({ component, row });
        },
      },
      timecardStatus: {
        accessorKey: 'status',
        size: 75,
        header: t(keys.common.STATUS),
        Cell: ({ cell }) =>
          labelCell({
            name: t(timecardStatusTypes[cell.getValue()]?.title),
            color: timecardStatusTypes[cell.getValue()]?.variant,
          }),
        GroupedCell: ({ cell, row }) => {
          const component = cell.getValue() ? (
            <Label
              noMargin
              rowSized={false}
              weight="semiBold"
              name={t(timecardStatusTypes[cell.getValue()]?.title)}
              color={timecardStatusTypes[cell.getValue()]?.variant}
            />
          ) : null;
          return groupedCell({ component, row });
        },
      },
      expenseStatus: {
        accessorKey: 'status',
        header: t(keys.common.STATUS),
        size: 75,
        Cell: ({ cell }) =>
          labelCell({
            name: t(expenseStatusTypes[cell.getValue()]?.title),
            color: expenseStatusTypes[cell.getValue()]?.variant,
          }),
        GroupedCell: ({ cell, row }) => {
          const component = cell.getValue() ? (
            <Label
              noMargin
              rowSized={false}
              weight="semiBold"
              name={t(expenseStatusTypes[cell.getValue()]?.title)}
              color={expenseStatusTypes[cell.getValue()]?.variant}
            />
          ) : null;
          return groupedCell({ component, row });
        },
      },
      dateTime: {
        accessorKey: 'dateTime',
        size: 100,
        header: t(keys.observations.DATE_AND_TIME),
        Cell: ({ cell }) =>
          dateCell({ cell }, ('primary', false, false, 'regular', 'sm')),
      },
      location: {
        accessorKey: 'location',
        header: t(keys.common.LOCATION),
        size: 100,
        Cell: ({ cell }) => textCell({ cell, size: 'xs', weight: 'regular' }),
        GroupedCell: ({ cell, row }) => {
          const component = textCell({ cell, weight: 'semiBold', size: 'sm' });
          return groupedCell({ component, row });
        },
      },
      nearMiss: {
        accessorKey: 'nearMiss',
        size: 50,
        header: 'NM',
        filterVariant: 'select',
        filterSelectOptions: [
          { value: 'true', label: t(keys.common.YES) },
          { value: 'false', label: t(keys.common.NO) },
        ],
        Cell: ({ cell }) => (
          <Icon
            baseClassName="material-icons-outlined"
            sx={{
              color: !cell.getValue() ? '#28a745' : '#e9222c',
              fontSize: '1.25rem',
            }}
          >
            {cell.getValue() ? 'check' : 'close'}
          </Icon>
        ),
        GroupedCell: ({ cell, row }) => {
          const name = !!cell.getValue()
            ? t(keys.common.YES)
            : t(keys.common.NO);
          const color = !cell.getValue() ? 'green' : 'red';
          const rowSized = false;
          const component = labelCell({
            name,
            color,
            rowSized,
          });
          return groupedCell({ component, row });
        },
      },
      lostTime: {
        accessorKey: 'lostTime',
        header: 'LT',
        size: 50,
        filterVariant: 'select',
        filterSelectOptions: [
          { value: 'true', label: t(keys.common.YES) },
          { value: 'false', label: t(keys.common.NO) },
        ],
        Cell: ({ cell }) => (
          <Icon
            baseClassName="material-icons-outlined"
            sx={{
              color: !cell.getValue() ? '#28a745' : '#e9222c',
              fontSize: '1.25rem',
            }}
          >
            {cell.getValue() ? 'check' : 'close'}
          </Icon>
        ),
        GroupedCell: ({ cell, row }) => {
          const name = !!cell.getValue()
            ? t(keys.common.YES)
            : t(keys.common.NO);
          const color = !!cell.getValue() ? 'red' : 'green';
          const rowSized = false;

          const component = labelCell({
            name,
            color,
            rowSized,
          });
          return groupedCell({ component, row });
        },
      },
      actionItemsOpen: {
        accessorFn: (row) => row?.actionItems?.id,
        accessorKey: 'actionItems.id',
        header: '',
        size: 80,
        enableSorting: false,
        Cell: ({ row }) => {
          return actionItemCheck({ row });
        },
      },
      risk: {
        accessorKey: 'impact',
        header: 'Risk',
        size: 75,
        Cell: ({ cell, row }) => {
          const data = row.original || {};
          const impact = cell.getValue() || data?.initialImpact || 0;
          const prob = data?.probability || data?.initialProbability || 0;
          const level = impact * prob;
          const risk = calculateRisk(level);
          return <Label name={risk.level} color={risk.textColor} />;
        },
        filterVariant: 'select',
        filterSelectOptions: Object.keys(riskLevels).map((key) => ({
          label: key,
          value: key,
        })),
      },
      riskLevel: {
        accessorKey: 'riskLevel',
        header: 'Risk Level',
        size: 100,
        Cell: ({ cell }) =>
          labelCell({
            name: cpaKeys[cell.getValue()],
            color: riskColors[cell.getValue()],
          }),
        GroupedCell: ({ cell, row }) => {
          const component = cell.getValue() ? (
            <Label
              noMargin
              rowSized={false}
              weight="semiBold"
              name={cpaKeys[cell.getValue()]}
              color={riskColors[cell.getValue()]}
            />
          ) : null;
          return groupedCell({ component, row });
        },
      },
      narrative: {
        accessorKey: 'narrative',
        size: 120,
        header: 'Narrative',
        Cell: ({ cell }) => textCell({ cell, size: 'xs', weight: 'regular' }),
      },
      cpaStatus: {
        size: 80,
        accessorKey: 'status',
        header: t(keys.common.STATUS),
        Cell: ({ cell }) =>
          labelCell({
            color: cpaStatusTypes[cell.getValue()]?.color,
            name: cpaStatusTypes[cell.getValue()]?.title,
          }),
      },
      cpaExtensionRequested: {
        accessorKey: 'extensionStatus',
        header: 'Ext Requested',
        enableColumnFilter: false,
        enableSorting: false,
        size: 90,
        Cell: ({ cell }) =>
          labelCell({
            color: extensionStatusTypes[cell.getValue()]?.color,
            name: extensionStatusTypes[cell.getValue()]?.title,
          }),
      },
    };

    return registry;
  }, [
    t,
    disableAssessmentSubject,
    groupedCell,
    allTemplateTypes,
    dateCellFromNow,
    dateCell,
    dateFormat,
    labelCell,
    allUsers,
    availableWorkspaces,
    allTemplates,
    allVehicles,
    workspaceCell,
    multiLabelCell,
    textCell,
    labelCellWithIcon,
    user?.id,
    externalIdCell,
  ]);
}
export const actionItemCheck = ({ row }) => {
  const data = row.original || {};
  const completeStatus = [
    'DONE',
    'DUPLICATE',
    'COMPLETE',
    'COMPLETED',
    'DE-ESCALATED',
    'CLOSED',
  ];
  const isComplete =
    completeStatus.includes(data.status) || data?.isDraft === false;

  if (isComplete) {
    return (
      <Icon
        baseClassName="material-icons-outlined"
        style={{
          fontSize: '1rem',
          color: customColors.GREEN,
          marginLeft: 'auto',
        }}
      >
        check_circle
      </Icon>
    );
  }

  const days = daysSince(data?.dateCreated);
  const color =
    days <= 15
      ? 'primary'
      : days <= 30
      ? 'yellow'
      : days <= 45
      ? 'orange'
      : 'red';
  const actionItems = (data?.actionItems || []).filter(
    (a) => a.status !== 'DUPLICATE' && a.status !== 'DONE',
  );

  return (
    <div className={styles.openContainer}>
      {!!actionItems.length && (
        <>
          <Icon style={{ fontSize: '1rem', color: customColors.RED }}>
            flag
          </Icon>
          <Chip
            label={actionItems?.length}
            size="small"
            sx={{ fontSize: 8 }}
            variant="outlined"
          />
        </>
      )}
      <Text noMargin size="xs" weight="semiBold" color={color}>
        {`${days} ${days <= 1 ? 'day' : 'days'}`}
      </Text>
    </div>
  );
};
